import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  defaultAxiosOptions,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export enum CampaignType {
  singleCode = 'single-code',
  multiCode = 'multi-code'
}

export type CampaignTypes = CampaignType.singleCode | CampaignType.multiCode

export enum DiscountType {
  percent = 1,
  value = 2
}

export type AuthorityShape = [string, string]

export interface CampaignState {
  singleCodeCampaigns: SingleCodeCampaignShape[]
  multiCodeCampaigns: MultiCodeCampaignShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  singleSingleCodeCampaign: SingleCodeCampaignDetailShape | null
  singleMultiCodeCampaign: MultiCodeCampaignDetailShape | null
  singleCampaignLoading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  multiCodeCampaignDiscountCodesLoading: LoadingStates
  campaignProducts: CampaignProductShape[]
  campaignProductsLoading: LoadingStates
  campaignOptions: CampaignOptions
  optionsLoading: LoadingStates
}

const initialState: CampaignState = {
  singleCodeCampaigns: [],
  multiCodeCampaigns: [],
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  singleSingleCodeCampaign: null,
  singleMultiCodeCampaign: null,
  singleCampaignLoading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  campaignProducts: [],
  campaignProductsLoading: 'idle',
  multiCodeCampaignDiscountCodesLoading: 'idle',
  campaignOptions: {
    authorities: []
  },
  optionsLoading: 'idle'
}

export interface CampaignParamShape {
  ordering: string
  page: number
}

type FetchCampaignListParams = CampaignParamShape

export type CampaignProductShape = [string, string]

type FetchCampaignProductListResponse = CampaignProductShape[]

interface FetchCampaignProductListRequest {
  salesChannelGroup: number
}

const fetchCampaignProductList = createAsyncThunk<
  FetchCampaignProductListResponse,
  FetchCampaignProductListRequest,
  { rejectValue: ValidationErrors }
>('campaign/fetchCampaignProductList', async (data, { rejectWithValue }) => {
  try {
    const { salesChannelGroup } = data
    const response = await getAxiosInstance().get<FetchCampaignProductListResponse>(
      `/api/v1/discount-campaigns/products/${salesChannelGroup}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

export interface CampaignOptions {
  authorities: AuthorityShape[]
}

interface FetchCampaignOptions {
  field_choices: CampaignOptions
}

const fetchCampaignOptions = createAxiosAsyncThunk<FetchCampaignOptions, undefined>(
  'campaign/fetchCampaignOptions',
  '/api/v1/discount-campaigns/single-code/',
  'OPTIONS'
)

interface FetchSingleCampaignRequest {
  id: number
}

interface DeleteCampaignRequest {
  id: number
}

export interface CampaignDetailShape {
  id: number | null
  name: string
  authorities: string[]
  start_date: Date | null
  end_date: Date | null
  sales_channel_group: number | null
  product_codes: []
  discount_type: number
  discount_amount: number
}

/* Single code campaigns */

export interface SingleCodeCampaignShape {
  id: number
  created: Date
  name: string
  start_date: Date | null
  end_date: Date | null
  authorities: AuthorityShape[]
  discount_code: string
}

export interface SingleCodeCampaignDetailShape extends CampaignDetailShape {
  discount_code: string
}

const fetchSingleCodeCampaignList = createAxiosAsyncThunkWithParams<
  ListResponse<SingleCodeCampaignShape>,
  FetchCampaignListParams | undefined
>('campaign/fetchSingleCodeCampaignList', '/api/v1/discount-campaigns/single-code/')

type FetchSingleSingleCodeCampaignResponse = SingleCodeCampaignDetailShape

const fetchSingleSingleCodeCampaign = createAsyncThunk<
  FetchSingleSingleCodeCampaignResponse,
  FetchSingleCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/fetchSingleSingleCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().get<FetchSingleSingleCodeCampaignResponse>(
      `/api/v1/discount-campaigns/single-code/${id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type CreateSingleCodeCampaignRequest = SingleCodeCampaignDetailShape

type CreateSingleCodeCampaignResponse = SingleCodeCampaignDetailShape

const createSingleCodeCampaign = createAsyncThunk<
  CreateSingleCodeCampaignResponse,
  CreateSingleCodeCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/createSingleCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateSingleCodeCampaignResponse>(
      `/api/v1/discount-campaigns/single-code/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type EditSingleCodeCampaignRequest = SingleCodeCampaignDetailShape

type EditSingleCodeCampaignResponse = SingleCodeCampaignDetailShape

const editSingleCodeCampaign = createAsyncThunk<
  EditSingleCodeCampaignResponse,
  EditSingleCodeCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/editSingleCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<EditSingleCodeCampaignResponse>(
      `/api/v1/discount-campaigns/single-code/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteSingleCodeCampaignResponse = Record<string, never>

const deleteSingleCodeCampaign = createAxiosAsyncThunkWithParams<
  DeleteSingleCodeCampaignResponse,
  DeleteCampaignRequest
>('campaign/deleteSingleCodeCampaign', '/api/v1/discount-campaigns/single-code/', 'DELETE')

/* Multi code campaigns */

export interface MultiCodeCampaignShape {
  id: number
  created: Date
  name: string
  start_date: Date | null
  end_date: Date | null
  authorities: AuthorityShape[]
  has_discount_codes_generated: boolean
}

export interface MultiCodeCampaignDetailShape extends CampaignDetailShape {
  discount_code_count: number
  has_discount_codes_generated: boolean
}

const fetchMultiCodeCampaignList = createAxiosAsyncThunkWithParams<
  ListResponse<MultiCodeCampaignShape>,
  FetchCampaignListParams | undefined
>('campaign/fetchMultiCodeCampaignList', '/api/v1/discount-campaigns/multi-code/')

type FetchSingleMultiCodeCampaignResponse = MultiCodeCampaignDetailShape

const fetchSingleMultiCodeCampaign = createAsyncThunk<
  FetchSingleMultiCodeCampaignResponse,
  FetchSingleCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/fetchSingleMultiCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().get<FetchSingleMultiCodeCampaignResponse>(
      `/api/v1/discount-campaigns/multi-code/${id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type CreateMultiCodeCampaignRequest = MultiCodeCampaignDetailShape

type CreateMultiCodeCampaignResponse = MultiCodeCampaignDetailShape

const createMultiCodeCampaign = createAsyncThunk<
  CreateMultiCodeCampaignResponse,
  CreateMultiCodeCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/createMultiCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateMultiCodeCampaignResponse>(
      `/api/v1/discount-campaigns/multi-code/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type EditMultiCodeCampaignRequest = MultiCodeCampaignDetailShape

type EditMultiCodeCampaignResponse = MultiCodeCampaignDetailShape

const editMultiCodeCampaign = createAsyncThunk<
  EditMultiCodeCampaignResponse,
  EditMultiCodeCampaignRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/editMultiCodeCampaign', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<EditMultiCodeCampaignResponse>(
      `/api/v1/discount-campaigns/multi-code/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface CreateMultiCodeCampaignDiscountCodesRequest {
  id: number
}

type CreateMultiCodeCampaignDiscountCodesResponse = MultiCodeCampaignShape

const createMultiCodeCampaignDiscountCodes = createAsyncThunk<
  CreateMultiCodeCampaignDiscountCodesResponse,
  CreateMultiCodeCampaignDiscountCodesRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/createMultiCodeCampaignDiscountCodes', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance().post<CreateMultiCodeCampaignDiscountCodesResponse>(
      `/api/v1/discount-campaigns/multi-code/${id}/discount-codes/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface FetchMultiCodeCampaignDiscountCodesRequest {
  id: number
}

type FetchMultiCodeCampaignDiscountCodesResponse = BlobPart

const fetchMultiCodeCampaignDiscountCodes = createAsyncThunk<
  FetchMultiCodeCampaignDiscountCodesResponse,
  FetchMultiCodeCampaignDiscountCodesRequest,
  {
    rejectValue: ValidationErrors
  }
>('campaign/fetchMultiCodeCampaignDiscountCodes', async (data, { rejectWithValue }) => {
  try {
    const { id } = data
    const response = await getAxiosInstance(
      'text',
      defaultAxiosOptions.timeout,
      'text/csv',
      'text/csv'
    ).get<FetchMultiCodeCampaignDiscountCodesResponse>(
      `/api/v1/discount-campaigns/multi-code/${id}/discount-codes/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteMultiCodeCampaignResponse = Record<string, never>

const deleteMultiCodeCampaign = createAxiosAsyncThunkWithParams<
  DeleteMultiCodeCampaignResponse,
  DeleteCampaignRequest
>('campaign/deleteMultiCodeCampaign', '/api/v1/discount-campaigns/multi-code/', 'DELETE')

const genericActions = actionGenerator('campaign')

const campaignSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchCampaignProductList.fulfilled, (state, { payload }) => {
        state.campaignProducts = payload
        state.campaignProductsLoading = 'fulfilled'
      })
      .addCase(fetchCampaignProductList.pending, state => {
        state.campaignProductsLoading = 'pending'
        state.campaignProducts = initialState.campaignProducts
      })
      .addCase(fetchCampaignProductList.rejected, state => {
        state.campaignProductsLoading = 'rejected'
      })

      .addCase(fetchCampaignOptions.fulfilled, (state, { payload }) => {
        state.campaignOptions = payload.field_choices
        state.optionsLoading = 'fulfilled'
      })
      .addCase(fetchCampaignOptions.pending, state => {
        state.optionsLoading = 'pending'
        state.campaignOptions = initialState.campaignOptions
      })
      .addCase(fetchCampaignOptions.rejected, state => {
        state.optionsLoading = 'rejected'
      })

      /* Single code campaigns */
      .addCase(fetchSingleCodeCampaignList.fulfilled, (state, { payload }) => {
        state.singleCodeCampaigns = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchSingleCodeCampaignList.pending, state => {
        state.singleCodeCampaigns = initialState.singleCodeCampaigns
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchSingleCodeCampaignList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchSingleSingleCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleSingleCodeCampaign = payload
        state.singleCampaignLoading = 'fulfilled'
      })
      .addCase(fetchSingleSingleCodeCampaign.pending, state => {
        state.singleSingleCodeCampaign = null
        state.singleCampaignLoading = 'pending'
      })
      .addCase(fetchSingleSingleCodeCampaign.rejected, state => {
        state.singleCampaignLoading = 'rejected'
      })

      .addCase(createSingleCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleSingleCodeCampaign = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createSingleCodeCampaign.pending, state => {
        state.singleSingleCodeCampaign = initialState.singleSingleCodeCampaign
        state.submitLoading = 'pending'
      })
      .addCase(createSingleCodeCampaign.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(editSingleCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleSingleCodeCampaign = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(editSingleCodeCampaign.pending, state => {
        state.singleSingleCodeCampaign = initialState.singleSingleCodeCampaign
        state.submitLoading = 'pending'
      })
      .addCase(editSingleCodeCampaign.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteSingleCodeCampaign.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteSingleCodeCampaign.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteSingleCodeCampaign.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      /* Multi code campaigns */
      .addCase(fetchMultiCodeCampaignList.fulfilled, (state, { payload }) => {
        state.multiCodeCampaigns = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchMultiCodeCampaignList.pending, state => {
        state.multiCodeCampaigns = initialState.multiCodeCampaigns
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
      })
      .addCase(fetchMultiCodeCampaignList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchSingleMultiCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleMultiCodeCampaign = payload
        state.singleCampaignLoading = 'fulfilled'
      })
      .addCase(fetchSingleMultiCodeCampaign.pending, state => {
        state.singleMultiCodeCampaign = null
        state.singleCampaignLoading = 'pending'
      })
      .addCase(fetchSingleMultiCodeCampaign.rejected, state => {
        state.singleCampaignLoading = 'rejected'
      })

      .addCase(createMultiCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleMultiCodeCampaign = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createMultiCodeCampaign.pending, state => {
        state.singleMultiCodeCampaign = initialState.singleMultiCodeCampaign
        state.submitLoading = 'pending'
      })
      .addCase(createMultiCodeCampaign.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(editMultiCodeCampaign.fulfilled, (state, { payload }) => {
        state.singleMultiCodeCampaign = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(editMultiCodeCampaign.pending, state => {
        state.singleMultiCodeCampaign = initialState.singleMultiCodeCampaign
        state.submitLoading = 'pending'
      })
      .addCase(editMultiCodeCampaign.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(createMultiCodeCampaignDiscountCodes.fulfilled, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'fulfilled'
      })
      .addCase(createMultiCodeCampaignDiscountCodes.pending, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'pending'
      })
      .addCase(createMultiCodeCampaignDiscountCodes.rejected, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'rejected'
      })

      .addCase(fetchMultiCodeCampaignDiscountCodes.fulfilled, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'fulfilled'
      })
      .addCase(fetchMultiCodeCampaignDiscountCodes.pending, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'pending'
      })
      .addCase(fetchMultiCodeCampaignDiscountCodes.rejected, state => {
        state.multiCodeCampaignDiscountCodesLoading = 'rejected'
      })

      .addCase(deleteMultiCodeCampaign.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteMultiCodeCampaign.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteMultiCodeCampaign.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default campaignSlice.reducer

export {
  fetchCampaignProductList,
  fetchCampaignOptions,
  fetchSingleCodeCampaignList,
  fetchSingleSingleCodeCampaign,
  createSingleCodeCampaign,
  editSingleCodeCampaign,
  deleteSingleCodeCampaign,
  fetchMultiCodeCampaignList,
  fetchSingleMultiCodeCampaign,
  createMultiCodeCampaign,
  editMultiCodeCampaign,
  deleteMultiCodeCampaign,
  createMultiCodeCampaignDiscountCodes,
  fetchMultiCodeCampaignDiscountCodes
}
